import { useState, useEffect, useCallback } from 'react';
import { updateService } from '@/services/updateService';

interface UseUpdateNotificationOptions {
  enabled?: boolean;
  pollingIntervalMs?: number;
}

export function useUpdateNotification(options: UseUpdateNotificationOptions = {}) {
  const { enabled = true, pollingIntervalMs } = options;
  const [showNotification, setShowNotification] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // Handle update found callback
  const handleUpdateFound = useCallback(() => {
    setShowNotification(true);
  }, []);

  // Handle refresh action
  const handleRefresh = useCallback(() => {
    updateService.refreshApp();
  }, []);

  // Handle dismiss action
  const handleDismiss = useCallback(() => {
    setShowNotification(false);
    // Stop polling once dismissed to avoid repeated notifications
    updateService.stopPolling();
  }, []);

  // Manual check for updates
  const checkForUpdates = useCallback(async () => {
    if (isChecking) return false;

    setIsChecking(true);
    try {
      const hasUpdate = await updateService.checkForUpdates();
      if (hasUpdate) {
        setShowNotification(true);
      }
      return hasUpdate;
    } catch (error) {
      console.warn('Manual update check failed:', error);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [isChecking]);

  // Development helper to simulate an update
  const simulateUpdate = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      updateService.simulateUpdate();
      setShowNotification(true);
    }
  }, []);

  // Start/stop polling based on enabled state
  useEffect(() => {
    if (!enabled) {
      updateService.stopPolling();
      return;
    }

    // Configure update service if custom polling interval provided
    if (pollingIntervalMs) {
      // Update service configuration would need to be implemented
    }

    // Start polling for updates
    updateService.startPolling(handleUpdateFound);

    // Cleanup on unmount
    return () => {
      updateService.stopPolling();
    };
  }, [enabled, pollingIntervalMs, handleUpdateFound]);

  return {
    showNotification,
    isChecking,
    checkForUpdates,
    handleRefresh,
    handleDismiss,
    simulateUpdate, // Development only
  };
}